import { useState } from "react";
import { SectionCard } from "@/components/ui/section-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  AppConfigSnapshot,
  useAppConfigsForApp,
  useAppConfigTags,
  useDeleteAppConfig,
} from "@/hooks/useAppConfigurations";
import { AppConfigDialog } from "./AppConfigDialog";
import { TagIcon } from "@/components/tags/TagIcon";

interface AppConfigListProps {
  bundleId: string;
  appName: string;
}

interface AppConfigRowProps {
  config: AppConfigSnapshot;
  onEdit: () => void;
  onDelete: () => void;
}

const AppConfigRow = ({ config, onEdit, onDelete }: AppConfigRowProps) => {
  const { data: tags = [], isLoading: isLoadingTags } = useAppConfigTags(
    config.policyId,
  );
  const keyCount = config.keys.length;
  const tagCount = tags.length;

  return (
    <div className="rounded-md border p-2.5 space-y-2">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{config.configName}</p>
          <p className="text-xs text-muted-foreground">
            {keyCount} {keyCount === 1 ? "key" : "keys"}
            <span aria-hidden="true"> · </span>
            {isLoadingTags ? "…" : `${tagCount} ${tagCount === 1 ? "tag" : "tags"}`}
          </p>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onEdit}
            aria-label={`Edit ${config.configName}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={onDelete}
            aria-label={`Delete ${config.configName}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.slice(0, 4).map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="gap-1 text-[10px] px-1.5 py-0"
              style={{
                backgroundColor: `${tag.color}20`,
                borderColor: tag.color,
                color: tag.color,
              }}
            >
              <TagIcon iconName={tag.icon} color={tag.color} size={6} />
              {tag.name}
            </Badge>
          ))}
          {tags.length > 4 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              +{tags.length - 4}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export const AppConfigList = ({ bundleId, appName }: AppConfigListProps) => {
  const { data: configs = [], isLoading } = useAppConfigsForApp(bundleId);
  const deleteConfig = useDeleteAppConfig(bundleId);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AppConfigSnapshot | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppConfigSnapshot | null>(null);

  const hasConfigs = configs.length > 0;

  return (
    <>
      <SectionCard
        title="App Configurations"
        description="Push key-value settings to this app on managed devices."
        headerAction={
          hasConfigs ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateOpen(true)}
              aria-label="Add app configuration"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-[68px] w-full rounded-md" />
            <Skeleton className="h-[68px] w-full rounded-md" />
          </div>
        ) : hasConfigs ? (
          <div className="space-y-2">
            {configs.map((config) => (
              <AppConfigRow
                key={config.policyId}
                config={config}
                onEdit={() => setEditing(config)}
                onDelete={() => setDeleteTarget(config)}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              No app configurations yet
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Configure
            </Button>
          </div>
        )}
      </SectionCard>

      <AppConfigDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        appName={appName}
        bundleId={bundleId}
      />

      {editing && (
        <AppConfigDialog
          open={true}
          onOpenChange={(open) => !open && setEditing(null)}
          appName={appName}
          bundleId={bundleId}
          existing={editing}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete App Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.configName}"? It
              will be removed from all assigned devices. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteConfig.mutateAsync(deleteTarget.policyId);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
